import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, Menu, X, Search, Filter, Heart, Zap, Moon, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AnimatedGroup } from '@/components/ui/animated-group';
import { CTA } from '@/components/ui/call-to-action';
import { DesignStackPricing } from '@/components/ui/pricing-demo';
import { Footer } from '@/components/ui/footer';
import LoginModal from '@/components/LoginModal';
import { motion, useInView } from 'framer-motion';
import { cn } from '@/lib/utils';

// Import BMC functionality
const BMC_URL = import.meta.env.VITE_BMC_URL || 'https://www.buymeacoffee.com/toolref';
const openBmc = (e?: React.MouseEvent) => {
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }
  window.open(BMC_URL, '_blank', 'noopener,noreferrer');
};

const transitionVariants = {
  item: {
    hidden: {
      opacity: 0,
      filter: 'blur(12px)',
      y: 12
    },
    visible: {
      opacity: 1,
      filter: 'blur(0px)',
      y: 0,
      transition: {
        type: 'spring' as const,
        bounce: 0.3,
        duration: 1.5
      }
    }
  }
};
const AboutSection = () => {
  const features = [{
    icon: <Search className="h-6 w-6" />,
    title: "Smart Search",
    description: "Find tools instantly with intelligent search that looks through names, descriptions, tags, and use cases."
  }, {
    icon: <Filter className="h-6 w-6" />,
    title: "Advanced Filtering",
    description: "Filter by category, pricing, platform, AI features, and use cases to find exactly what you need."
  }, {
    icon: <Heart className="h-6 w-6" />,
    title: "Personal Collections",
    description: "Save your favorite tools and access them anytime. Your favorites are stored locally and sync across sessions."
  }, {
    icon: <Zap className="h-6 w-6" />,
    title: "Lightning Fast",
    description: "No loading screens, no waiting. Browse hundreds of tools instantly with our optimized interface."
  }];
  const steps = [{
    number: "1",
    title: "Browse or Search",
    description: "Start by browsing our curated collection or search for specific tools and use cases."
  }, {
    number: "2",
    title: "Filter & Compare",
    description: "Use our advanced filters to narrow down tools by category, pricing, platform, and features."
  }, {
    number: "3",
    title: "Discover & Save",
    description: "Explore detailed information about each tool and save your favorites for quick access later."
  }];
  return <section className="py-24 bg-background relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-b from-surface/50 to-background"></div>
      
      <div className="container mx-auto px-4 relative">
        {/* Mission */}
        <AnimatedGroup preset="fade" className="text-center mb-20">
          <h2 className="text-3xl md:text-4xl font-poppins font-bold text-text-primary mb-6">
            Our Mission
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed font-inter">
            We believe designers should spend their time creating, not searching for tools. DesignStack eliminates the friction of tool discovery by providing a clean, fast, and comprehensive directory of design tools.
          </p>
        </AnimatedGroup>

        {/* Features */}
        <div className="mb-20">
          <AnimatedGroup preset="fade" className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-poppins font-semibold text-text-primary">
              Why DesignStack?
            </h2>
          </AnimatedGroup>
          <AnimatedGroup preset="slide" className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, index) => <div key={index} className="bg-card rounded-xl border border-border p-6 hover:shadow-md transition-shadow duration-base">
                <div className="flex items-start space-x-4">
                  <div className="h-12 w-12 rounded-lg bg-accent-light flex items-center justify-center text-accent flex-shrink-0">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-poppins font-semibold text-text-primary mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-text-secondary font-inter">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>)}
          </AnimatedGroup>
        </div>

        {/* How it works */}
        <div className="bg-card rounded-xl border border-border p-8">
          <AnimatedGroup preset="fade" className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-poppins font-semibold text-text-primary">
              How It Works
            </h2>
          </AnimatedGroup>
          <AnimatedGroup preset="slide" className="space-y-6">
            {steps.map((step, index) => <div key={index} className="flex items-start space-x-4">
                <div className="h-10 w-10 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-sm font-semibold flex-shrink-0">
                  {step.number}
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary mb-2 font-poppins">{step.title}</h3>
                  <p className="text-text-secondary font-inter">{step.description}</p>
                </div>
              </div>)}
          </AnimatedGroup>
        </div>
      </div>
    </section>;
};
export function HeroSection() {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  return <>
            <HeroHeader onLoginClick={() => setIsLoginModalOpen(true)} />
            <LoginModal 
              isOpen={isLoginModalOpen} 
              onClose={() => setIsLoginModalOpen(false)} 
            />
            <main className="overflow-hidden">
                <div aria-hidden className="z-[2] absolute inset-0 pointer-events-none isolate opacity-50 contain-strict hidden lg:block">
                    <div className="w-[35rem] h-[80rem] -translate-y-[350px] absolute left-0 top-0 -rotate-45 rounded-full bg-[radial-gradient(68.54%_68.72%_at_55.02%_31.46%,hsla(0,0%,85%,.08)_0,hsla(0,0%,55%,.02)_50%,hsla(0,0%,45%,0)_80%)]" />
                    <div className="h-[80rem] absolute left-0 top-0 w-56 -rotate-45 rounded-full bg-[radial-gradient(50%_50%_at_50%_50%,hsla(0,0%,85%,.06)_0,hsla(0,0%,45%,.02)_80%,transparent_100%)] [translate:5%_-50%]" />
                    <div className="h-[80rem] -translate-y-[350px] absolute left-0 top-0 w-56 -rotate-45 bg-[radial-gradient(50%_50%_at_50%_50%,hsla(0,0%,85%,.04)_0,hsla(0,0%,45%,.02)_80%,transparent_100%)]" />
                </div>
                <section>
                    <div className="relative pt-24 md:pt-36">
                        {/* Background removed - using solid dark mode background color */}
                        <div aria-hidden className="absolute inset-0 -z-10 size-full [background:radial-gradient(125%_125%_at_50%_100%,transparent_0%,var(--background)_75%)]" />
                        <div className="mx-auto max-w-7xl px-6">
                            <div className="text-center sm:mx-auto lg:mr-auto lg:mt-0">
                                <AnimatedGroup variants={transitionVariants}>
                                    <Link to="/tools-explainer" className="hover:bg-background dark:hover:border-t-border bg-muted group mx-auto flex w-fit items-center gap-4 rounded-full border p-1 pl-4 shadow-md shadow-black/5 transition-all duration-300 dark:border-t-white/5 dark:shadow-zinc-950">
                                        <span className="text-foreground text-sm font-inter">Discover the Best Design Tools</span>
                                        <span className="dark:border-background block h-4 w-0.5 border-l bg-white dark:bg-zinc-700"></span>

                                        <div className="bg-background group-hover:bg-muted size-6 overflow-hidden rounded-full duration-500">
                                            <div className="flex w-12 -translate-x-1/2 duration-500 ease-in-out group-hover:translate-x-0">
                                                <span className="flex size-6">
                                                    <ArrowRight className="m-auto size-3" />
                                                </span>
                                                <span className="flex size-6">
                                                    <ArrowRight className="m-auto size-3" />
                                                </span>
                                            </div>
                                        </div>
                                    </Link>
                        
                                    <h1 className="mt-8 max-w-4xl mx-auto text-balance text-6xl md:text-7xl lg:mt-16 xl:text-[5.25rem] font-bold font-poppins">
                                        Find the Perfect Design Tools for Your Workflow
                                    </h1>
                                    <p className="mx-auto mt-8 max-w-2xl text-balance text-lg text-muted-foreground font-inter">
                                        Discover, compare, and find the best UX/UI and product design tools. From prototyping to analytics, we help designers find the right tools fast.
                                    </p>
                                </AnimatedGroup>

                <AnimatedGroup variants={{
                container: {
                  visible: {
                    transition: {
                      staggerChildren: 0.05,
                      delayChildren: 0.75
                    }
                  }
                },
                ...transitionVariants
              }} className="mt-12 flex flex-col items-center justify-center gap-4 md:flex-row">
                                     <div key={1} className="bg-primary/10 rounded-[14px] border border-primary/20 p-0.5">
                                         <Link to="/unlock">
                                             <Button
                                                 size="lg" 
                                                 className="rounded-xl px-5 text-base bg-gradient-to-r from-primary to-primary-glow hover:from-primary-glow hover:to-primary text-primary-foreground font-semibold font-poppins"
                                             >
                                                 <Zap className="mr-2 h-4 w-4" />
                                                 <span className="text-nowrap">Get Lifetime Access</span>
                                             </Button>
                                         </Link>
                                     </div>
                                     <div key={2} className="bg-foreground/10 rounded-[14px] border p-0.5">
                                         <Button 
                                           variant="outline" 
                                           size="lg" 
                                           className="rounded-xl px-5 text-base font-inter"
                                           onClick={() => setIsLoginModalOpen(true)}
                                         >
                                             <span className="text-nowrap">Login</span>
                                         </Button>
                                     </div>
                                </AnimatedGroup>
                            </div>
                        </div>

                        <AnimatedGroup variants={{
            container: {
              visible: {
                transition: {
                  staggerChildren: 0.05,
                  delayChildren: 0.75
                }
              }
            },
            ...transitionVariants
          }}>
                            <motion.div className="relative -mr-56 mt-8 overflow-hidden px-2 sm:mr-0 sm:mt-12 md:mt-20" initial={{
              opacity: 0,
              y: 40
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 1.2,
              ease: "easeOut"
            }}>
                                <div aria-hidden className="bg-gradient-to-b to-background absolute inset-0 z-10 from-transparent from-35%" />
                                <motion.div className="inset-shadow-2xs ring-background dark:inset-shadow-white/20 bg-background relative mx-auto max-w-6xl overflow-hidden rounded-2xl border p-4 shadow-lg shadow-zinc-950/15 ring-1" initial={{
                scale: 0.9,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                duration: 1,
                delay: 1.4,
                ease: "easeOut"
              }} whileHover={{
                scale: 1.02,
                transition: {
                  duration: 0.3
                }
              }}>
                                    <img 
                                      className="bg-background aspect-[8/5] relative rounded-2xl border border-border/25 shadow-2xl w-full h-auto" 
                                      src="/lovable-uploads/d884324f-71e7-4a47-bc84-da6547bb3d87.png" 
                                      alt="DesignStack Dashboard - Browse and discover design tools" 
                                      width="1116" 
                                      height="627" 
                                      loading="eager"
                                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 90vw, 1116px"
                                      style={{ maxWidth: '1116px', height: 'auto', objectFit: 'cover' }}
                                      decoding="sync"
                                    />
                                </motion.div>
                            </motion.div>
                        </AnimatedGroup>
                    </div>
                </section>
                
                {/* CTA Section */}
                <CTA />
                
                {/* About Section */}
                <AboutSection />
                
                {/* Footer */}
                <Footer />
            </main>
        </>;
}
const menuItems = [];
const HeroHeader = ({ onLoginClick }: { onLoginClick: () => void }) => {
  const [menuState, setMenuState] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isDark, setIsDark] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  React.useEffect(() => {
    const isDarkMode = document.documentElement.classList.contains('dark');
    setIsDark(isDarkMode);
  }, []);

  const toggleTheme = () => {
    document.documentElement.classList.toggle('dark');
    setIsDark(!isDark);
  };
  return <header>
            <nav data-state={menuState && 'active'} className="fixed z-20 w-full px-2 group">
                <div className={cn('mx-auto mt-2 max-w-6xl px-6 transition-all duration-300 lg:px-12', isScrolled && 'bg-background/50 max-w-4xl rounded-2xl border backdrop-blur-lg lg:px-5')}>
                    <div className="relative flex flex-wrap items-center justify-between gap-6 py-3 lg:gap-0 lg:py-4">
                        <div className="flex w-full justify-between lg:w-auto">
                            <Link to="/" aria-label="home" className="flex items-center space-x-2">
                                <Logo />
                            </Link>

                            <button onClick={() => setMenuState(!menuState)} aria-label={menuState == true ? 'Close Menu' : 'Open Menu'} className="relative z-20 -m-2.5 -mr-4 block cursor-pointer p-2.5 lg:hidden">
                                <Menu className="in-data-[state=active]:rotate-180 group-data-[state=active]:scale-0 group-data-[state=active]:opacity-0 m-auto size-6 duration-200" />
                                <X className="group-data-[state=active]:rotate-0 group-data-[state=active]:scale-100 group-data-[state=active]:opacity-100 absolute inset-0 m-auto size-6 -rotate-180 scale-0 opacity-0 duration-200" />
                            </button>
                        </div>

                        <div className="absolute inset-0 m-auto hidden size-fit lg:block">
                            <ul className="flex gap-8 text-sm">
                                {menuItems.map((item, index) => <li key={index}>
                                        <Link to={item.href} className="text-muted-foreground hover:text-accent-foreground block duration-150 font-inter">
                                            <span>{item.name}</span>
                                        </Link>
                                    </li>)}
                            </ul>
                        </div>

                        <div className="bg-background group-data-[state=active]:block lg:group-data-[state=active]:flex mb-6 hidden w-full flex-wrap items-center justify-end space-y-8 rounded-3xl border p-6 shadow-2xl shadow-zinc-300/20 md:flex-nowrap lg:m-0 lg:flex lg:w-fit lg:gap-6 lg:space-y-0 lg:border-transparent lg:bg-transparent lg:p-0 lg:shadow-none dark:shadow-none dark:lg:bg-transparent">
                            <div className="lg:hidden">
                                <ul className="space-y-6 text-base">
                                    {menuItems.map((item, index) => <li key={index}>
                                            <Link to={item.href} className="text-muted-foreground hover:text-accent-foreground block duration-150 font-inter">
                                                <span>{item.name}</span>
                                            </Link>
                                        </li>)}
                                </ul>
                            </div>
                            <div className="flex w-full flex-col space-y-3 sm:flex-row sm:gap-3 sm:space-y-0 md:w-fit">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={toggleTheme}
                                  className="text-text-secondary hover:text-primary"
                                >
                                  {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className={cn(isScrolled && 'lg:hidden', 'font-inter')}
                                  onClick={onLoginClick}
                                >
                                    <span>Login</span>
                                </Button>
                                <Link to="/unlock" aria-label="Get lifetime access to all design tools">
                                    <Button 
                                        size="sm" 
                                        className={cn(isScrolled && 'lg:hidden', 'font-inter')}
                                    >
                                        <span>Get Access</span>
                                    </Button>
                                </Link>
                                <Link to="/unlock" aria-label="Get lifetime access to all design tools">
                                    <Button 
                                        size="sm" 
                                        className={cn(isScrolled ? 'lg:inline-flex' : 'hidden', 'font-inter')}
                                    >
                                        <span>Get Lifetime Access</span>
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </nav>
        </header>;
};
const Logo = ({
  className
}: {
  className?: string;
}) => {
  return <div className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-bold text-lg">
                D
            </div>
            <span className="text-xl font-bold font-poppins">DesignStack</span>
        </div>;
};